import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { 
    ChevronRightIcon, 
    ArrowDownTrayIcon, 
    DocumentTextIcon, 
    BookOpenIcon, 
    ClipboardDocumentCheckIcon,
    GlobeAltIcon
} from '@heroicons/react/24/outline';
import SEO from '../SEO/SEO';

const ResourcesPage = () => {
    const { t } = useTranslation();
    const [banner, setBanner] = useState(null);
    const [bannerLoading, setBannerLoading] = useState(true);
    const [resources, setResources] = useState([]);
    const [resourcesLoading, setResourcesLoading] = useState(true);
    
    // UI state for active tab/language
    const [selectedBrochureLang, setSelectedBrochureLang] = useState('Telugu');

    const fallbackBanner = {
        title: 'Resources & Downloads',
        subtitle: 'Access important brochures, customer checklists, and guidebooks to make your housing finance journey seamless.',
        image_url: '/img/loan-banners/Home-Construction-Banner-1775033164974-103342859.webp',
        primary_button_text: 'Explore Below',
        primary_button_link: '#resources-section'
    };

    // Fetch banner
    useEffect(() => {
        const fetchBanner = async () => {
            try {
                setBannerLoading(true);
                const response = await axios.get('/api/admin/banners/page/resources');
                const banners = response.data.data || [];
                if (banners.length > 0) {
                    setBanner(banners[0]);
                } else {
                    setBanner(fallbackBanner);
                }
            } catch (error) {
                console.error('Error fetching banner:', error);
                setBanner(fallbackBanner);
            } finally {
                setBannerLoading(false);
            }
        };

        fetchBanner();
    }, []);

    // Fetch resources
    useEffect(() => {
        const fetchResources = async () => {
            try {
                setResourcesLoading(true);
                const response = await axios.get('/api/admin/resources/active');
                if (response.data.success) {
                    setResources(response.data.data || []);
                }
            } catch (error) {
                console.error('Error fetching active resources:', error);
            } finally {
                setResourcesLoading(false);
            }
        };

        fetchResources();
    }, []);

    // Grouping logic for UI
    const brochures = resources.filter(res => res.category === 'Product Brochures');
    
    // Get all categories other than 'Product Brochures'
    const nonBrochureCategories = [...new Set(resources
        .filter(res => res.category !== 'Product Brochures')
        .map(res => res.category)
    )];
    
    // Get unique languages dynamically from active brochures
    const availableLangs = [...new Set(brochures
        .map(b => b.language)
        .filter(lang => lang !== null && lang !== '')
    )];

    // Auto-select first available language if current selection is empty/unavailable
    useEffect(() => {
        if (availableLangs.length > 0 && !availableLangs.includes(selectedBrochureLang)) {
            setSelectedBrochureLang(availableLangs[0]);
        }
    }, [availableLangs, selectedBrochureLang]);

    const brochureLanguages = availableLangs.length > 0 ? availableLangs : ['Telugu', 'Tamil', 'Kannada', 'English'];

    const filteredBrochures = brochures.filter(b => b.language === selectedBrochureLang);

    return (
        <div className="min-h-screen bg-slate-50">
            <SEO
                title="Resources & Downloads | Aham Housing Finance"
                description="Download Aham Product Brochures (Telugu, Tamil, Kannada, English) and Customer Checklists (Login and Disbursement checklists)."
                keywords="Aham resources, home loan brochures, home loan checklists, download home loan documents, vastu tips brochure, home construction brochure, login checklist, disbursement checklist"
                canonical="https://ahamhfc.com/resources"
            />

            {/* Hero Banner Section */}
            <section className="relative min-h-[50vh] flex items-center justify-start overflow-hidden">
                {bannerLoading ? (
                    <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
                        <div className="text-white text-center">
                            <div className="animate-pulse">
                                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        <div
                            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                            style={{
                              backgroundImage: `url('${banner?.image_url ? (banner.image_url.startsWith('http') || banner.image_url.startsWith('/') ? banner.image_url : `/${banner.image_url}`) : (banner?.backgroundImage || fallbackBanner.image_url)}')`,
                            }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-800/80 to-transparent"></div>
                        </div>

                        <div className="relative z-10 container mx-auto px-4 py-16 md:py-24 text-left w-full">
                            <motion.div
                                initial={{ opacity: 0, x: -50 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                className="max-w-3xl"
                            >
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.1, duration: 0.6 }}
                                    className="flex items-center space-x-2 mb-6"
                                >
                                    <Link to="/" className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium">
                                        Home
                                    </Link>
                                    <ChevronRightIcon className="h-4 w-4 text-white/60" />
                                    <span className="text-yellow-400 text-sm font-semibold">Resources</span>
                                </motion.div>

                                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                                    {banner?.title || fallbackBanner.title}
                                </h1>
                                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                                    {banner?.subtitle || fallbackBanner.subtitle}
                                </p>

                                <div className="flex gap-4">
                                    <a href="#resources-section">
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-3 rounded-xl font-bold text-sm md:text-base transition-all duration-300 shadow-2xl"
                                        >
                                            {banner?.primary_button_text || fallbackBanner.primary_button_text}
                                        </motion.button>
                                    </a>
                                </div>
                            </motion.div>
                        </div>
                    </>
                )}
            </section>

            {/* Main Content Section */}
            <section id="resources-section" className="py-20">
                <div className="container mx-auto px-4 space-y-20">
                    
                    {/* Grid Layout for Brochures & Checklists */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        
                        {/* Column 1: Product Brochures (Takes 7 Cols on Large Screen) */}
                        <div className="lg:col-span-7 space-y-8">
                            <div className="border-b border-slate-200 pb-4">
                                <div className="flex items-center gap-3 mb-2">
                                    <BookOpenIcon className="h-7 w-7 text-primary-600" />
                                    <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight">
                                        Product Brochures
                                    </h2>
                                </div>
                                <p className="text-slate-500 text-sm">
                                    Download product guides and vastu recommendations in your preferred language
                                </p>
                            </div>

                            {/* Language Navigation Tabs */}
                            <div className="flex flex-wrap gap-2 p-1.5 bg-white border border-slate-200/60 rounded-2xl shadow-sm max-w-md">
                                {brochureLanguages.map(lang => {
                                    const hasData = brochures.some(b => b.language === lang);
                                    return (
                                        <button
                                            key={lang}
                                            disabled={!hasData}
                                            onClick={() => setSelectedBrochureLang(lang)}
                                            className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 flex items-center gap-2 ${
                                                selectedBrochureLang === lang
                                                    ? 'bg-slate-950 text-white shadow-md'
                                                    : hasData
                                                        ? 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                                        : 'text-slate-300 cursor-not-allowed'
                                            }`}
                                        >
                                            <GlobeAltIcon className="h-4 w-4 shrink-0" />
                                            {lang}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Brochures Lists container */}
                            {resourcesLoading ? (
                                <div className="space-y-4">
                                    {[1, 2].map(i => (
                                        <div key={i} className="h-20 bg-white border border-slate-100 rounded-2xl animate-pulse"></div>
                                    ))}
                                </div>
                            ) : filteredBrochures.length === 0 ? (
                                <div className="bg-white rounded-3xl p-8 border border-slate-200/60 text-center shadow-sm">
                                    <p className="text-slate-400 font-medium">No brochures uploaded for {selectedBrochureLang} yet.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    {filteredBrochures.map(brochure => (
                                        <motion.div
                                            key={brochure.id}
                                            whileHover={{ y: -5, shadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)' }}
                                            className="bg-white p-6 rounded-3xl border border-slate-200/60 flex flex-col justify-between hover:border-primary-400 transition-all duration-300 relative group overflow-hidden shadow-sm"
                                        >
                                            {/* Glow Accent */}
                                            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary-500 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                            
                                            <div>
                                                <div className="h-12 w-12 bg-primary-50 text-primary-600 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-primary-600 group-hover:text-white transition-colors duration-300">
                                                    <DocumentTextIcon className="h-6 w-6" />
                                                </div>
                                                <span className="text-xs font-semibold text-primary-500 uppercase tracking-widest block mb-1">
                                                    {brochure.language}
                                                </span>
                                                <h3 className="font-bold text-slate-800 text-lg mb-4 line-clamp-2 leading-snug">
                                                    {brochure.title}
                                                </h3>
                                            </div>

                                            <a
                                                href={brochure.file_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                download
                                                className="w-full flex items-center justify-center gap-2 bg-slate-50 border border-slate-200/60 text-slate-700 hover:bg-slate-950 hover:text-white hover:border-transparent py-3 rounded-2xl text-sm font-bold transition-all duration-300 mt-auto"
                                            >
                                                <ArrowDownTrayIcon className="h-4 w-4" />
                                                Download PDF
                                            </a>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Column 2: Other Resource Categories (Takes 5 Cols on Large Screen) */}
                        <div className="lg:col-span-5 space-y-12">
                            {resourcesLoading ? (
                                <div className="space-y-8">
                                    <div className="border-b border-slate-200 pb-4">
                                        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse"></div>
                                    </div>
                                    <div className="space-y-4">
                                        {[1, 2].map(i => (
                                            <div key={i} className="h-20 bg-white border border-slate-100 rounded-2xl animate-pulse"></div>
                                        ))}
                                    </div>
                                </div>
                            ) : nonBrochureCategories.length === 0 ? (
                                <div className="bg-white rounded-3xl p-8 border border-slate-200/60 text-center shadow-sm">
                                    <p className="text-slate-400 font-medium">No other resources uploaded yet.</p>
                                </div>
                            ) : (
                                nonBrochureCategories.map(categoryName => {
                                    const categoryItems = resources.filter(res => res.category === categoryName);
                                    return (
                                        <div key={categoryName} className="space-y-6">
                                            <div className="border-b border-slate-200 pb-4">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <ClipboardDocumentCheckIcon className="h-7 w-7 text-primary-600" />
                                                    <h2 className="text-3xl font-extrabold text-slate-800 tracking-tight">
                                                        {categoryName}
                                                    </h2>
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                {categoryItems.map(item => (
                                                    <motion.div
                                                        key={item.id}
                                                        whileHover={{ scale: 1.02 }}
                                                        className="bg-white p-5 rounded-3xl border border-slate-200/60 flex items-center justify-between gap-6 hover:shadow-md transition-all duration-300 shadow-sm"
                                                    >
                                                        <div className="flex items-center gap-4 min-w-0">
                                                            <div className="h-12 w-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shrink-0">
                                                                <ClipboardDocumentCheckIcon className="h-6 w-6" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <h3 className="font-bold text-slate-800 text-base md:text-lg truncate">
                                                                    {item.title}
                                                                </h3>
                                                                <p className="text-slate-400 text-xs mt-0.5">
                                                                    Format: {item.file_url ? item.file_url.split('.').pop().toUpperCase() : 'PDF'}
                                                                </p>
                                                            </div>
                                                        </div>

                                                        <a
                                                            href={item.file_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            download
                                                            className="p-3 bg-slate-50 border border-slate-200/60 hover:bg-slate-950 hover:text-white hover:border-transparent text-slate-700 rounded-2xl transition-all duration-300 shrink-0"
                                                            title={`Download ${item.title}`}
                                                        >
                                                            <ArrowDownTrayIcon className="h-5 w-5" />
                                                        </a>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                    </div>

                </div>
            </section>
        </div>
    );
};

export default ResourcesPage;
